/**
 * Custom Size Selector Component - Metafield-Based Approach
 * Uses product variant metafields to determine custom size ranges
 * Stores exact dimensions as line item properties
 * 
 * Features:
 * - Dynamic ATC button states (SELECT A SIZE, UNAVAILABLE, ADD TO CART)
 * - Dropdown value restoration when switching between tabs
 * - Persistent selection memory across tab switches
 * - ATC price restoration - shows original text with price when variants are available
 * - Smart separator formatting - ensures proper "•" separator in ATC button text
 * - Real-time price updates - updates all price displays when toggling between tabs
 * - Global metafields integration for variant management
 */

class CustomSizeSelector {
  constructor(element) {
    this.element = element;
    this.tabs = this.element.querySelectorAll('[data-size-tab]');
    this.contents = this.element.querySelectorAll('[data-size-content]');
    this.standardToggle = this.element.querySelector('[data-standard-size-toggle]');
    this.standardOptions = this.element.querySelectorAll('[data-popout-option]');
    this.widthInput = this.element.querySelector('[data-custom-width-input]');
    this.heightInput = this.element.querySelector('[data-custom-height-input]');
    this.plusButtons = this.element.querySelectorAll('[data-custom-size-plus]');
    this.minusButtons = this.element.querySelectorAll('[data-custom-size-minus]');
    
    // Hidden form inputs
    this.sizeInput = this.element.querySelector('[data-custom-size-input]');
    this.widthProperty = this.element.querySelector('[data-custom-width-property]');
    this.heightProperty = this.element.querySelector('[data-custom-height-property]');
    this.sizeTypeProperty = this.element.querySelector('[data-size-type-property]');
    this.optionValueDisplay = document.querySelector('[data-option-value]');
    
    this.activeTab = 'standard';
    
    // Store selected values to restore when switching tabs
    this.selectedStandardValue = null;
    this.selectedStandardText = null;
    
    // Load product data and custom size ranges from metafields
    this.productData = this.getProductData();
    this.customSizeRanges = [];
    
    this.init();
  }
  
  /**
   * Get product data from the page
   */
  getProductData() {
    try {
      const productJson = document.querySelector('[data-product-json]');
      if (productJson) {
        return JSON.parse(productJson.textContent);
      }
      
      // Fallback: try to get from window.product if available
      if (window.product) {
        return window.product;
      }
      
      console.warn('Product data not found');
      return null;
    } catch (error) {
      console.error('Error parsing product data:', error);
      return null;
    }
  }
  
  /**
   * Load custom size ranges from global metafields
   * Uses the globally available window.productMetafields object
   */
  async loadCustomSizeRanges() {
    if (!window.productMetafields) {
      console.warn('Global product metafields not available');
      return this.processVariantsFallback();
    }
    
    try {
      const customRanges = [];
      
      // Use the global helper function to get custom variants
      const customVariants = window.getCustomVariants();
      
      customVariants.forEach(variant => {
        if (variant.ranges) {
          customRanges.push({
            variantId: variant.id,
            variantName: variant.title,
            minWidth: variant.ranges.minWidth,
            maxWidth: variant.ranges.maxWidth,
            minHeight: variant.ranges.minHeight,
            maxHeight: variant.ranges.maxHeight,
            price: variant.price,
            available: variant.available
          });
        }
      });
      
      // Sort by width range for consistent ordering
      customRanges.sort((a, b) => a.minWidth - b.minWidth);
      
      console.log('Loaded custom size ranges from global metafields:', customRanges);
      return customRanges;
      
    } catch (error) {
      console.error('Error loading custom size ranges from global metafields:', error);
      return this.processVariantsFallback();
    }
  }
  
  /**
   * Check if a variant is a custom size variant
   */
  isCustomVariant(variantId) {
    return window.isCustomVariant && window.isCustomVariant(variantId);
  }
  
  /**
   * Fallback method when global metafields are not available
   * Looks for variants with "Custom" in the title
   */
  processVariantsFallback() {
    const customRanges = [];
    
    this.productData.variants.forEach(variant => {
      // Simple check: look for "Custom" in variant title
      if (variant.title && variant.title.toLowerCase().includes('custom')) {
        // Try to extract size ranges from variant title
        // Expected format: "Custom 50-100cm x 80-150cm" or similar
        const ranges = this.extractRangesFromTitle(variant.title);
        
        customRanges.push({
          variantId: variant.id,
          variantName: variant.title,
          minWidth: ranges.minWidth,
          maxWidth: ranges.maxWidth,
          minHeight: ranges.minHeight,
          maxHeight: ranges.maxHeight,
          price: variant.price,
          available: variant.available
        });
      }
    });
    
    console.log('Loaded custom size ranges (fallback method):', customRanges);
    return customRanges;
  }
  
  /**
   * Extract size ranges from variant title
   */
  extractRangesFromTitle(title) {
    // Default ranges if parsing fails
    let minWidth = 50, maxWidth = 200, minHeight = 50, maxHeight = 200;
    
    // Try to extract dimensions like "50-100cm x 80-150cm" or "50-100 x 80-150"
    const rangePattern = /(\d+)-(\d+).*?x.*?(\d+)-(\d+)/i;
    const match = title.match(rangePattern);
    
    if (match) {
      minWidth = parseInt(match[1]);
      maxWidth = parseInt(match[2]);
      minHeight = parseInt(match[3]);
      maxHeight = parseInt(match[4]);
    }
    
    return { minWidth, maxWidth, minHeight, maxHeight };
  }

  async init() {
    // Store instance on element for debugging
    this.element.customSizeSelector = this;
    
    this.bindEvents();
    this.updateFormValues(); // Initialize with empty values
    
    // Load custom size ranges asynchronously
    this.customSizeRanges = await this.loadCustomSizeRanges();
    
    // Set initial ATC state - default to "SELECT A SIZE" since no size is selected initially
    // The active tab defaults to 'standard' as set in constructor
    this.setProductSelectSize();
    
    // Debug logging
    console.log('Custom Size Selector initialized:', {
      sizeInput: this.sizeInput,
      standardOptions: this.standardOptions.length,
      customSizeRanges: this.customSizeRanges.length,
      productData: !!this.productData,
      initialState: 'SELECT A SIZE'
    });
  }
  
  bindEvents() {
    // Tab switching
    this.tabs.forEach(tab => {
      tab.addEventListener('click', this.handleTabSwitch.bind(this));
    });
    
    // Standard size selection - use a different approach
    // Instead of overriding click events, let's listen for the theme's popout events
    this.bindStandardSizeEvents();
    
    // Custom size inputs
    if (this.widthInput) {
      this.widthInput.addEventListener('input', this.handleCustomSizeChange.bind(this));
      this.widthInput.addEventListener('blur', this.validateCustomSize.bind(this));
    }
    
    if (this.heightInput) {
      this.heightInput.addEventListener('input', this.handleCustomSizeChange.bind(this));
      this.heightInput.addEventListener('blur', this.validateCustomSize.bind(this));
    }
    
    // Plus/Minus buttons
    this.plusButtons.forEach(button => {
      button.addEventListener('click', this.handlePlusClick.bind(this));
    });
    
    this.minusButtons.forEach(button => {
      button.addEventListener('click', this.handleMinusClick.bind(this));
    });
  }
  
  bindStandardSizeEvents() {
    // Try to work with the theme's popout component instead of against it
    const popout = this.element.querySelector('.select-popout');
    
    if (popout) {
      // Listen for theme's popout selection events if they exist
      popout.addEventListener('popout:select', this.handlePopoutSelect.bind(this));
      
      // Also listen for clicks but don't interfere as much
      this.standardOptions.forEach(option => {
        option.addEventListener('click', this.handleStandardSizeSelection.bind(this));
      });
      
      // Listen for when popout closes naturally
      const toggle = popout.querySelector('[data-popout-toggle]');
      if (toggle) {
        toggle.addEventListener('click', () => {
          console.log('Popout toggle clicked');
        });
      }
    } else {
      // Fallback to original method if no popout found
      this.standardOptions.forEach(option => {
        option.addEventListener('click', this.handleStandardSizeSelection.bind(this));
      });
    }
  }
  
  handlePopoutSelect(event) {
    console.log('Theme popout select event:', event);
    // Handle theme's native popout selection if it exists
    if (event.detail && event.detail.value) {
      this.updateSizeSelection(event.detail.value, event.detail.text || event.detail.value);
    }
  }
  
  handleTabSwitch(event) {
    const tabType = event.target.getAttribute('data-size-tab');
    this.switchTab(tabType);
  }
  
  switchTab(tabType) {
    this.activeTab = tabType;
    
    // Update tab states
    this.tabs.forEach(tab => {
      tab.classList.toggle('size-tab--active', tab.getAttribute('data-size-tab') === tabType);
    });
    
    // Update content states
    this.contents.forEach(content => {
      content.classList.toggle('size-content--active', content.getAttribute('data-size-content') === tabType);
    });
    
    // Clear form values when switching tabs
    this.clearFormValues();
    this.updateFormValues();
    
    // Set appropriate ATC state based on tab
    if (tabType === 'standard') {
      // Standard tab - restore previous selection or set to "SELECT A SIZE"
      if (this.selectedStandardValue && this.selectedStandardText) {
        // Restore the previous selection
        this.restoreStandardSelection();
        this.setProductAvailable(); // Set to available state since we have a selection
        this.updateAddToCartText('available'); // Show original text with price
      } else {
        this.setProductSelectSize(); // No previous selection, show "SELECT A SIZE"
      }
    } else if (tabType === 'custom') {
      // Custom tab - check if there are valid dimensions already entered
      const width = parseInt(this.widthInput?.value) || 0;
      const height = parseInt(this.heightInput?.value) || 0;
      const pricingRange = this.findPricingRange(width, height);
      
      if (width > 0 && height > 0 && pricingRange) {
        // Valid custom size already entered, show as available with original price text
        this.setProductAvailable();
        this.updateAddToCartText('available');
      } else {
        // No valid custom size, show "SELECT A SIZE"
        this.setProductSelectSize();
      }
    }
    
    //this.updatePricing();
  }
  
  handleStandardSizeSelection(event) {
    // Don't prevent default immediately - let the theme handle the dropdown behavior
    console.log('Standard size selection event:', event.type, event.target);
    
    if(event.target.tagName.toLowerCase() == 'a') {
      const selectedText = event.target.querySelector('span.select-option__name').getAttribute('data-value');
    }else{
      const selectedText = event.target.getAttribute('data-value');
    }
    
    const selectedValue = event.target.getAttribute('data-value');
    const selectedText = event.target.textContent.trim();
    
    // Use our centralized update method
    this.updateSizeSelection(selectedValue, selectedText);
    
    // Let the event bubble up to theme handlers, but prevent navigation
    event.preventDefault();
  }
  
  updateSizeSelection(selectedValue, selectedText) {
    console.log(`Updating size selection: "${selectedValue}" (${selectedText})`);

    if(selectedText.includes('$')){
      selectedText = selectedText.split('$')[0].trim();
    }

    // Validate that we have a value
    if (!selectedValue) {
      console.error('No value found for selected standard size');
      return;
    }
    
    // Store the selected values for later restoration
    this.selectedStandardValue = selectedValue;
    this.selectedStandardText = selectedText;
    
    // Get variant price for the selected size
    const variant = this.getVariantBySize(selectedValue);
    const variantPrice = variant ? this.formatPrice(variant.price) : '';
    
    // Update the dropdown display
    if (this.standardToggle) {
      const toggleText = this.standardToggle.querySelector('[data-popout-toggle-text]');
      const togglePrice = this.standardToggle.querySelector('[data-popout-toggle-price]');
      
      if (toggleText) {
        toggleText.textContent = selectedText;
        console.log(`Updated dropdown display to: "${selectedText}"`);
      }
      
      if (togglePrice) {
        if (variantPrice) {
          togglePrice.textContent = variantPrice;
          togglePrice.style.display = 'block';
          console.log(`Updated toggle price to: "${variantPrice}"`);
        } else {
          togglePrice.style.display = 'none';
        }
      }
    }
    
    // Update active state
    this.standardOptions.forEach(option => {
      const isActive = option.getAttribute('data-value') === selectedValue;
      option.closest('.select-popout__item').classList.toggle('is-active', isActive);
      if (isActive) {
        option.setAttribute('aria-current', 'true');
      } else {
        option.removeAttribute('aria-current');
      }
    });
    
    // Update form values for custom size selector
    if (this.sizeInput) {
      this.sizeInput.value = selectedValue;
      console.log(`Set sizeInput.value to: "${this.sizeInput.value}"`);
    }
    
    if (this.sizeTypeProperty) {
      this.sizeTypeProperty.value = 'standard';
    }
    
    // Clear custom size properties
    if (this.widthProperty) this.widthProperty.value = '';
    if (this.heightProperty) this.heightProperty.value = '';
    
    // Update display
    if (this.optionValueDisplay) {
      this.optionValueDisplay.textContent = selectedText;
      this.optionValueDisplay.style.color = ''; // Reset any error colors
    }
    
    // Enable ATC button since a valid standard size is selected
    this.setProductAvailable();
    this.updateAddToCartText('available');
    
    // Update price displays for the new selection
    //this.updatePriceDisplays();
    
    // Also update any standard Shopify variant selectors that might exist
    this.updateStandardVariantSelectors(selectedValue);
    
    // Trigger variant change event
    this.triggerVariantChange();
  }
  
  /**
   * Restore previously selected standard size when switching back to standard tab
   */
  restoreStandardSelection() {
    if (!this.selectedStandardValue || !this.selectedStandardText) {
      return;
    }
    
    console.log(`Restoring standard selection: "${this.selectedStandardValue}" (${this.selectedStandardText})`);
    
    // Get variant price for the selected size
    const variant = this.getVariantBySize(this.selectedStandardValue);
    const variantPrice = variant ? this.formatPrice(variant.price) : '';
    
    // Update the dropdown display
    if (this.standardToggle) {
      const toggleText = this.standardToggle.querySelector('[data-popout-toggle-text]');
      const togglePrice = this.standardToggle.querySelector('[data-popout-toggle-price]');
      
      if (toggleText) {
        toggleText.textContent = this.selectedStandardText;
        console.log(`Restored dropdown display to: "${this.selectedStandardText}"`);
      }
      
      if (togglePrice) {
        if (variantPrice) {
          togglePrice.textContent = variantPrice;
          togglePrice.style.display = 'block';
          console.log(`Restored toggle price to: "${variantPrice}"`);
        } else {
          togglePrice.style.display = 'none';
        }
      }
    }
    
    // Update active state for options
    this.standardOptions.forEach(option => {
      const isActive = option.getAttribute('data-value') === this.selectedStandardValue;
      option.closest('.select-popout__item').classList.toggle('is-active', isActive);
      if (isActive) {
        option.setAttribute('aria-current', 'true');
      } else {
        option.removeAttribute('aria-current');
      }
    });
    
    // Update form values
    if (this.sizeInput) {
      this.sizeInput.value = this.selectedStandardValue;
    }
    
    if (this.sizeTypeProperty) {
      this.sizeTypeProperty.value = 'standard';
    }
    
    // Clear custom size properties
    if (this.widthProperty) this.widthProperty.value = '';
    if (this.heightProperty) this.heightProperty.value = '';
    
    // Update display
    if (this.optionValueDisplay) {
      this.optionValueDisplay.textContent = this.selectedStandardText;
      this.optionValueDisplay.style.color = '';
    }
    
    // Update any standard Shopify variant selectors that might exist
    this.updateStandardVariantSelectors(this.selectedStandardValue);
  }
  
  /**
   * Update standard Shopify variant selectors to maintain compatibility
   */
  updateStandardVariantSelectors(selectedValue) {
    // Look for standard Shopify variant selector inputs
    const standardSelectors = document.querySelectorAll('[data-single-option-selector]');
    
    standardSelectors.forEach(selector => {
      // Check if this is for the same option (size)
      const selectorName = selector.getAttribute('name');
      const ourSelectorName = this.sizeInput.getAttribute('name');
      
      if (selectorName && ourSelectorName && 
          selectorName.includes('options[') && 
          ourSelectorName.includes('options[') &&
          selector !== this.sizeInput) {
        
        // Extract option name from both selectors to compare
        const selectorOptionName = selectorName.match(/options\[([^\]]+)\]/)?.[1];
        const ourOptionName = ourSelectorName.match(/options\[([^\]]+)\]/)?.[1];
        
        if (selectorOptionName === ourOptionName) {
          console.log(`Updating standard selector: ${selectorName} with value: ${selectedValue}`);
          selector.value = selectedValue;
          
          // Trigger change event on the standard selector
          const changeEvent = new Event('change', { bubbles: true });
          selector.dispatchEvent(changeEvent);
        }
      }
    });
  }
  
  handleCustomSizeChange(event) {
    if (this.activeTab !== 'custom') return;
    
    const width = parseInt(this.widthInput?.value) || 0;
    const height = parseInt(this.heightInput?.value) || 0;
    
    // Get max values from the input attributes (set by metafields)
    const maxWidth = this.widthInput?.getAttribute('max') ? parseInt(this.widthInput.getAttribute('max')) : null;
    const maxHeight = this.heightInput?.getAttribute('max') ? parseInt(this.heightInput.getAttribute('max')) : null;
    
    // Check if dimensions exceed maximum limits
    if (maxWidth && width > maxWidth) {
      this.showDimensionError('width', width, maxWidth);
      this.setProductUnavailable();
      return;
    }
    
    if (maxHeight && height > maxHeight) {
      this.showDimensionError('height', height, maxHeight);
      this.setProductUnavailable();
      return;
    }
    
    // Find the appropriate pricing range using metafield data
    const pricingRange = this.findPricingRange(width, height);
    
    if (width > 0 && height > 0 && pricingRange) {
      // Set the hidden variant value to the pricing range
      this.sizeInput.value = pricingRange.variantName;
      
      // Store exact dimensions in line item properties
      this.widthProperty.value = width;
      this.heightProperty.value = height;
      this.sizeTypeProperty.value = 'custom';
      
      // Update display with range info
      if (this.optionValueDisplay) {
        const rangeInfo = this.getRangeDisplay(pricingRange);
        this.optionValueDisplay.textContent = `Custom ${width}×${height}cm (${rangeInfo})`;
        this.optionValueDisplay.style.color = ''; // Reset error color
      }
      
      // Log for debugging
      console.log(`Selected range: ${pricingRange.variantName} for ${width}×${height}cm`);
      
      // Set product as available since we found a valid range
      this.setProductAvailable();
      this.updateAddToCartText('available');
      
      // Update price displays for the new custom size
      //this.updatePriceDisplays();
      
      // Trigger variant change to update price
      this.triggerVariantChange();
    } else if (width > 0 && height > 0) {
      // Dimensions entered but no matching range found
      console.warn(`No pricing range found for ${width}×${height}cm`);
      this.showNoRangeError(width, height);
      this.setProductUnavailable();
    } else {
      // No dimensions entered or incomplete - show "SELECT A SIZE"
      this.clearCustomValues();
      this.setProductSelectSize();
    }
  }
  
  /**
   * Show error when dimensions don't match any available range
   */
  showNoRangeError(width, height) {
    if (this.optionValueDisplay) {
      this.optionValueDisplay.textContent = `Custom ${width}×${height}cm - Size not available`;
      this.optionValueDisplay.style.color = '#dc2626'; // Red color for error
    }
    
    // Clear form values since no valid range was found
    this.clearCustomValues();
  }
  
  /**
   * Show error when dimensions exceed maximum limits set by metafields
   */
  showDimensionError(dimension, value, maxValue) {
    if (this.optionValueDisplay) {
      const dimensionName = dimension === 'width' ? 'Width' : 'Height';
      this.optionValueDisplay.textContent = `${dimensionName} ${value}cm exceeds maximum of ${maxValue}cm`;
      this.optionValueDisplay.style.color = '#dc2626'; // Red color for error
    }
    
    // Clear form values since dimensions exceed limits
    this.clearCustomValues();
  }
  
  /**
   * Set product as unavailable (disable add to cart)
   */
  setProductUnavailable() {
    // Find the product form wrapper
    const formWrapper = this.sizeInput?.form || document.querySelector('[data-form-wrapper], [data-product-form], .product-form');
    const addToCartButton = document.querySelector('.add-to-cart-button[data-add-to-cart]');
    const addToCartText = document.querySelector('.add-to-cart-button [data-add-to-cart-text]');
    
    if (formWrapper) {
      formWrapper.classList.add('variant--unavailable');
      console.log('Set product form as unavailable');
    }
    
    if (addToCartButton) {
      addToCartButton.setAttribute('disabled', true);
      addToCartButton.classList.add('is-disabled');
      console.log('Disabled add to cart button');
    }
    
    this.updateAddToCartText('unavailable');
  }
  
  /**
   * Set product as available (enable add to cart)
   */
  setProductAvailable() {
    // Find the product form wrapper
    const formWrapper = this.sizeInput?.form || document.querySelector('[data-form-wrapper], [data-product-form], .product-form');
    const addToCartButton = document.querySelector('.add-to-cart-button [data-add-to-cart]');
    
    if (formWrapper) {
      formWrapper.classList.remove('variant--unavailable');
      formWrapper.classList.remove('variant--soldout');
      console.log('Set product form as available');
    }
    
    if (addToCartButton) {
      addToCartButton.removeAttribute('disabled');
      addToCartButton.classList.remove('is-disabled');
      console.log('Enabled add to cart button');
    }
  }
  
  /**
   * Set product to "select a size" state (enabled but prompting for selection)
   */
  setProductSelectSize() {
    // Find the product form wrapper
    const formWrapper = this.sizeInput?.form || document.querySelector('[data-form-wrapper], [data-product-form], .product-form');
    const addToCartButton = document.querySelector('.add-to-cart-button[data-add-to-cart]');
    
    if (formWrapper) {
      formWrapper.classList.remove('variant--unavailable');
      formWrapper.classList.remove('variant--soldout');
      console.log('Set product form to select size state');
    }
    
    if (addToCartButton) {
      addToCartButton.setAttribute('disabled', true);
      addToCartButton.classList.add('is-disabled');
      console.log('Disabled add to cart button for size selection');
    }
    // Reset standard size toggle to default state when no selection
    if (this.activeTab === 'standard' && this.standardToggle) {
      const toggleText = this.standardToggle.querySelector('[data-popout-toggle-text]');
      const togglePrice = this.standardToggle.querySelector('[data-popout-toggle-price]');
      
      if (toggleText) {
        // Reset to default placeholder text (this will be set from Liquid template)
        const defaultText = toggleText.getAttribute('data-default-text') || 
                           'Choose From Popular Sizes';
        toggleText.textContent = defaultText;
      }
      
      if (togglePrice) {
        togglePrice.style.display = 'none'; // Hide price when no selection
      }
    }
    
    this.updateAddToCartText('selectSize');
  }
  
  /**
   * Update Add to Cart button text based on state
   */
  updateAddToCartText(state) {
    const addToCartText = document.querySelector('.add-to-cart-button [data-add-to-cart-text]');
    
    if (!addToCartText) return;
    
    // Store original text if not already stored
    if (!addToCartText.hasAttribute('data-original-text')) {
      addToCartText.setAttribute('data-original-text', addToCartText.textContent);
    }
    
    const originalText = addToCartText.getAttribute('data-original-text');
    
    switch (state) {
      case 'selectSize':
        addToCartText.textContent = "SELECT A SIZE";
        break;
      case 'unavailable':
        addToCartText.textContent = "UNAVAILABLE";
        break;
      case 'available':
      default:
        // Restore original text, ensuring proper separator formatting
        let displayText = originalText;
        
        // If we're restoring text and it should have price info, ensure proper separator
        if (originalText && this.shouldAddSeparator(originalText)) {
          displayText = this.formatWithSeparator(originalText);
        }
        
        addToCartText.textContent = displayText;
        break;
    }
  }
  
  /**
   * Check if the text should have a separator added
   */
  shouldAddSeparator(text) {
    if (!text || text.includes('•')) return false;
    
    // Add separator if it's an "ADD TO CART" button without separator
    const lowercaseText = text.toLowerCase();
    return lowercaseText.includes('add to cart') || lowercaseText.includes('add to bag');
  }
  
  /**
   * Format text with proper separator
   */
  formatWithSeparator(text) {
    // If text doesn't have a separator but should, add one with current price
    if (this.shouldAddSeparator(text)) {
      const currentPrice = this.getCurrentVariantPrice();
      const formattedPrice = currentPrice ? this.formatPrice(currentPrice) : '';
      
      if (formattedPrice) {
      // Add separator and current price
      return 'ADD TO CART' + ' • ' + formattedPrice;
      } else {
      // No price available, just add separator
      return 'ADD TO CART' + ' •';
      }
    }
    return text;
  }
  
  findPricingRange(width, height) {
    // Find the appropriate pricing range based on width and height using metafield data
    for (const range of this.customSizeRanges) {
      if (width >= range.minWidth && 
          width <= range.maxWidth && 
          height >= range.minHeight && 
          height <= range.maxHeight &&
          range.available) {
        return range;
      }
    }
    return null;
  }
  
  /**
   * Get the current variant's price
   */
  getCurrentVariantPrice() {
    if (this.activeTab === 'standard' && this.selectedStandardValue) {
      // Get price for selected standard variant
      const variant = this.getVariantBySize(this.selectedStandardValue);
      return variant ? variant.price : null;
    } else if (this.activeTab === 'custom') {
      // Get price for current custom size range
      const width = parseInt(this.widthInput?.value) || -1;
      const height = parseInt(this.heightInput?.value) || -1;
      const pricingRange = this.findPricingRange(width, height);
      return pricingRange ? pricingRange.price : null;
    }
    return null;
  }
  
  /**
   * Get variant by size value
   */
  getVariantBySize(sizeValue) {
    if (!this.productData || !this.productData.variants) return null;
    
    return this.productData.variants.find(variant => {
      return variant.title === sizeValue || 
             variant.option1 === sizeValue || 
             variant.option2 === sizeValue || 
             variant.option3 === sizeValue;
    });
  }
  
  /**
   * Format price using Shopify's money format
   */
  formatPrice(priceInCents) {
    if (!priceInCents) return '';
    
    // Use theme's money format from window.theme
    const moneyFormat = window.theme?.moneyFormat || '$';
    const price = (priceInCents / 100).toFixed(2);
    return moneyFormat.replace('\{\{amount\}\}', price);
  }
  
  /**
   * Update all price displays for current selection
   */
  updatePriceDisplays() {
    const currentPrice = this.getCurrentVariantPrice();
    
    if (!currentPrice) {
      console.log('No price available for current selection');
      return;
    }
    
    const formattedPrice = this.formatPrice(currentPrice);
    console.log(`Updating price displays to: ${formattedPrice}`);
    
    // Update ATC button price
    this.updateATCButtonPrice(formattedPrice);
  }
  
  /**
   * Update ATC button with price
   */
  updateATCButtonPrice(formattedPrice) {
    const addToCartText = document.querySelector('.add-to-cart-button [data-add-to-cart-text]');
    
    if (!addToCartText) return;
    
    // Get base ATC text (usually "ADD TO CART")
    const baseText = this.getBaseATCText();
    const newText = `${baseText} • ${formattedPrice}`;
    
    // Store as new original text so separator logic works correctly
    addToCartText.setAttribute('data-original-text', newText);
    addToCartText.textContent = newText;
    
    console.log(`Updated ATC button text to: "${newText}"`);
  }
  
  /**
   * Get base ATC text (without price)
   */
  getBaseATCText() {
    // Check theme settings for ATC button text
    if (window.theme?.atcButtonShowPrice === false) {
      return 'ADD TO CART';
    }
    
    // Try to extract base text from original text
    const addToCartText = document.querySelector('.add-to-cart-button [data-add-to-cart-text]');
    const originalText = addToCartText?.getAttribute('data-original-text') || 'ADD TO CART';
    
    // Remove price and separator from original text to get base text
    return originalText.replace(/\s*•\s*[\$£€¥₹][\d.,]+.*$/, '').trim() || 'ADD TO CART';
  }
  
  /**
   * Update main product price display
   */
  updateProductPriceDisplay(formattedPrice) {
    const priceElements = document.querySelectorAll('[data-product-price], .product__price, .price');
    
    priceElements.forEach(element => {
      if (element.classList.contains('price__current') || element.hasAttribute('data-product-price')) {
        element.textContent = formattedPrice;
        console.log('Updated product price display');
      }
    });
  }
  
  /**
   * Update other price-related elements
   */
  updateOtherPriceElements(formattedPrice) {
    // Update any variant price displays
    const variantPriceElements = document.querySelectorAll('[data-variant-price]');
    variantPriceElements.forEach(element => {
      element.textContent = formattedPrice;
    });
    
    // Trigger custom price update event for theme compatibility
    document.dispatchEvent(new CustomEvent('variantPriceUpdate', {
      detail: { 
        price: formattedPrice,
        selector: this 
      }
    }));
  }
  
  /**
   * Check if a variant is a custom size variant
   */
  isCustomVariant(variantId) {
    return window.isCustomVariant && window.isCustomVariant(variantId);
  }
  
  /**
   * Get all standard (non-custom) variants
   */
  getStandardVariants() {
    if (!this.productData || !this.productData.variants) return [];
    
    return this.productData.variants.filter(variant => {
      // Use global helper function instead of direct metafield access
      return !window.isCustomVariant || !window.isCustomVariant(variant.id);
    });
  }
  
  /**
   * Get range info for display purposes
   */
  getRangeDisplay(range) {
    const widthDisplay = range.maxWidth === 9999 ? `${range.minWidth}cm+` : `${range.minWidth}-${range.maxWidth}cm`;
    const heightDisplay = range.maxHeight === 9999 ? 'any height' : `${range.minHeight}-${range.maxHeight}cm`;
    return `${widthDisplay} width, ${heightDisplay}`;
  }
  
  clearCustomValues() {
    this.sizeInput.value = '';
    this.widthProperty.value = '';
    this.heightProperty.value = '';
    this.sizeTypeProperty.value = '';
    
    if (this.optionValueDisplay) {
      this.optionValueDisplay.textContent = '';
      this.optionValueDisplay.style.color = ''; // Reset color
    }
  }
  
  handlePlusClick(event) {
    const type = event.target.getAttribute('data-custom-size-plus');
    const input = type === 'width' ? this.widthInput : this.heightInput;
    if (input) {
      const currentValue = parseInt(input.value) || 0;
      const maxValue = parseInt(input.getAttribute('max')) || 999;
      if (currentValue < maxValue) {
        input.value = currentValue + 1;
        this.handleCustomSizeChange();
      }
    }
  }
  
  handleMinusClick(event) {
    const type = event.target.getAttribute('data-custom-size-minus');
    const input = type === 'width' ? this.widthInput : this.heightInput;
    if (input) {
      const currentValue = parseInt(input.value) || 0;
      const minValue = parseInt(input.getAttribute('min')) || 1;
      if (currentValue > minValue) {
        input.value = currentValue - 1;
        this.handleCustomSizeChange();
      }
    }
  }
  
  validateCustomSize(event) {
    const input = event.target;
    const value = parseInt(input.value);
    const min = parseInt(input.getAttribute('min')) || 1;
    const max = input.getAttribute('max') ? parseInt(input.getAttribute('max')) : null;
    
    if (isNaN(value) || value < min) {
      input.value = min;
    } else if (max && value > max) {
      input.value = max;
    }
    
    this.handleCustomSizeChange();
  }
  
  updateFormValues() {
    if (this.activeTab === 'custom') {
      const width = this.widthInput ? parseInt(this.widthInput.value) || 0 : 0;
      const height = this.heightInput ? parseInt(this.heightInput.value) || 0 : 0;
      
      if (width > 0 && height > 0) {
        // Find the appropriate pricing range
        const pricingRange = this.findPricingRange(width, height);
        
        if (pricingRange) {
          // Use the hidden variant name for the size input
          this.sizeInput.value = pricingRange.variantName;
          this.sizeTypeProperty.value = 'custom';
          this.widthProperty.value = width;
          this.heightProperty.value = height;
          
          // Update display with actual dimensions
          if (this.optionValueDisplay) {
            this.optionValueDisplay.textContent = `Custom ${width}×${height}cm`;
          }
          
          this.triggerVariantChange();
        }
      } else {
        // Clear values if dimensions are not valid
        this.clearFormValues();
      }
    }
  }
  
  clearFormValues() {
    this.sizeInput.value = '';
    this.sizeTypeProperty.value = '';
    this.widthProperty.value = '';
    this.heightProperty.value = '';
    
    if (this.optionValueDisplay) {
      this.optionValueDisplay.textContent = '';
    }
  }
  
  /**
   * Reset stored standard size selection (useful for testing or clearing state)
   */
  resetStoredSelection() {
    this.selectedStandardValue = null;
    this.selectedStandardText = null;
    console.log('Cleared stored standard size selection');
  }
  
  updatePricing() {
    // Update all price displays based on current selection
    this.updatePriceDisplays();
    
    // With metafield-based variants, Shopify handles pricing automatically
    // We just need to trigger the variant change event
    if (this.activeTab === 'custom') {
      const width = this.widthInput ? parseInt(this.widthInput.value) || 0 : 0;
      const height = this.heightInput ? parseInt(this.heightInput.value) || 0 : 0;
      
      if (width > 0 && height > 0) {
        const pricingRange = this.findPricingRange(width, height);
        if (pricingRange) {
          console.log(`Using variant: ${pricingRange.variantName} (ID: ${pricingRange.variantId}) for ${width}×${height}cm`);
          console.log(`Price: $${(pricingRange.price / 100).toFixed(2)}`);
        } else {
          console.warn(`No available custom size range for ${width}×${height}cm`);
        }
      }
    } else if (this.activeTab === 'standard' && this.selectedStandardValue) {
      const variant = this.getVariantBySize(this.selectedStandardValue);
      if (variant) {
        console.log(`Using standard variant: ${variant.title} - Price: $${(variant.price / 100).toFixed(2)}`);
      }
    }
  }
  
  closeDropdown() {
    // Close the dropdown if it's open - but let the theme handle the actual closing
    const popout = this.element.querySelector('.select-popout');
    if (popout) {
      const toggle = popout.querySelector('[data-popout-toggle]');
      
      if (toggle) {
        // Just set aria-expanded to false and let theme handle the rest
        toggle.setAttribute('aria-expanded', 'false');
        
        // Trigger a click on the toggle to let the theme's native logic handle closing
        // But prevent it from reopening by temporarily disabling the toggle
        const originalPointerEvents = toggle.style.pointerEvents;
        toggle.style.pointerEvents = 'none';
        
        setTimeout(() => {
          toggle.style.pointerEvents = originalPointerEvents;
        }, 100);
        
        console.log('Closed dropdown - let theme handle the rest');
      }
      
      // Don't manually hide the list - let the theme do it
      // This was causing the issue where it wouldn't work after first close
    }
  }
  
  triggerVariantChange() {
    console.log(`Triggering variant change - Tab: ${this.activeTab}, Value: ${this.sizeInput.value}`);
    
    // Trigger custom event for our size selector
    const customEvent = new CustomEvent('variantChange', {
      detail: {
        sizeType: this.activeTab,
        value: this.sizeInput.value,
        width: this.widthProperty?.value,
        height: this.heightProperty?.value
      },
      bubbles: true
    });
    document.dispatchEvent(customEvent);
    
    // Trigger standard Shopify variant change events
    if (this.sizeInput) {
      // Dispatch change event on our size input
      const changeEvent = new Event('change', { bubbles: true });
      this.sizeInput.dispatchEvent(changeEvent);
      
      // Also dispatch input event for more compatibility
      const inputEvent = new Event('input', { bubbles: true });
      this.sizeInput.dispatchEvent(inputEvent);
    }
    
    // Look for and trigger Shopify's variant selector scripts
    //this.triggerShopifyVariantSelection();
  }
  
  /**
   * Trigger Shopify's native variant selection system
   */
  triggerShopifyVariantSelection() {
    // Look for common Shopify variant selector patterns
    const productForm = this.sizeInput?.form || document.querySelector('[data-product-form], .product-form');
    
    if (productForm) {
      // Trigger events that Shopify themes commonly listen for
      const events = ['change', 'variant:change', 'variantChange'];
      
      events.forEach(eventType => {
        const event = new CustomEvent(eventType, {
          detail: { variant: this.sizeInput.value },
          bubbles: true
        });
        productForm.dispatchEvent(event);
      });
      
      console.log('Triggered Shopify variant selection events on product form');
    }
    
    // Also check for theme-specific variant handling
    if (typeof window.theme !== 'undefined' && window.theme.ProductForm) {
      console.log('Found theme ProductForm - triggering update');
      // Some themes have global variant update functions
    }
    
    // Dispatch global variant change event that many themes listen for
    window.dispatchEvent(new CustomEvent('variant:changed', {
      detail: {
        variant: this.sizeInput.value,
        sizeType: this.activeTab
      }
    }));
  }
  
  /**
   * Test method to verify the custom size selector is working
   * Can be called from browser console: customSizeSelector.test()
   */
  test() {
    console.log('Testing Custom Size Selector...');
    console.log('Element:', this.element);
    console.log('Size Input:', this.sizeInput);
    console.log('Standard Options Count:', this.standardOptions.length);
    console.log('Custom Size Ranges:', this.customSizeRanges);
    console.log('Active Tab:', this.activeTab);
    
    // Test standard size selection
    if (this.standardOptions.length > 0) {
      console.log('Testing standard size selection...');
      const firstOption = this.standardOptions[0];
      const testValue = firstOption.getAttribute('data-value');
      console.log(`Simulating selection of: ${testValue}`);
      
      // Simulate click
      firstOption.click();
      
      setTimeout(() => {
        console.log('After standard selection:', {
          sizeInputValue: this.sizeInput?.value,
          sizeType: this.sizeTypeProperty?.value,
          displayText: this.optionValueDisplay?.textContent
        });
      }, 100);
    }
    
    return 'Test completed - check console for results';
  }
}

// Initialize custom size selectors when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const selectors = document.querySelectorAll('[data-custom-size-selector]');
  selectors.forEach((selector, index) => {
    const instance = new CustomSizeSelector(selector);
    
    // Make first instance available globally for testing
    if (index === 0) {
      window.customSizeSelector = instance;
      console.log('Custom Size Selector available globally as window.customSizeSelector');
    }
  });
});

// Re-initialize on AJAX updates (for themes that use AJAX)
document.addEventListener('shopify:section:load', function(event) {
  const selectors = event.target.querySelectorAll('[data-custom-size-selector]');
  selectors.forEach(selector => {
    new CustomSizeSelector(selector);
  });
});

// Global test functions for debugging
window.testCustomSizeSelector = function() {
  console.log('=== CUSTOM SIZE SELECTOR TEST ===');
  
  const element = document.querySelector('[data-custom-size-selector]');
  if (!element) {
    console.error('Custom size selector element not found');
    return;
  }
  
  console.log('Element found:', element);
  
  // Test metafields
  if (window.testMetafields) {
    window.testMetafields();
  }
  
  // Test custom size selector instance
  const selector = element.customSizeSelector || new CustomSizeSelector(element);
  if (selector) {
    console.log('Custom size selector instance:', selector);
    console.log('Custom size ranges loaded:', selector.customSizeRanges);
    console.log('Active tab:', selector.activeTab);
    
    // Test finding a pricing range
    if (selector.customSizeRanges.length > 0) {
      console.log('Testing findPricingRange(100, 150):', selector.findPricingRange(100, 150));
    }
  }
  
  console.log('=== END TEST ===');
};

window.switchToCustomTab = function() {
  const element = document.querySelector('[data-custom-size-selector]');
  const selector = element?.customSizeSelector;
  if (selector) {
    selector.switchTab('custom');
    console.log('Switched to custom tab');
  }
};

window.switchToStandardTab = function() {
  const element = document.querySelector('[data-custom-size-selector]');
  const selector = element?.customSizeSelector;
  if (selector) {
    selector.switchTab('standard');
    console.log('Switched to standard tab');
  }
};

// Test function for unavailable state
window.testUnavailableSize = function(width = 1000, height = 1000) {
  const element = document.querySelector('[data-custom-size-selector]');
  const selector = element?.customSizeSelector;
  if (selector) {
    selector.switchTab('custom');
    
    if (selector.widthInput && selector.heightInput) {
      selector.widthInput.value = width;
      selector.heightInput.value = height;
      
      // Trigger the change event
      const changeEvent = new Event('input', { bubbles: true });
      selector.widthInput.dispatchEvent(changeEvent);
      selector.heightInput.dispatchEvent(changeEvent);
      
      console.log(`Testing unavailable size: ${width}×${height}cm`);
    }
  }
};

// Test function for available size
window.testAvailableSize = function(width = 100, height = 150) {
  const element = document.querySelector('[data-custom-size-selector]');
  const selector = element?.customSizeSelector;
  if (selector) {
    selector.switchTab('custom');
    
    if (selector.widthInput && selector.heightInput) {
      selector.widthInput.value = width;
      selector.heightInput.value = height;
      
      // Trigger the change event
      const changeEvent = new Event('input', { bubbles: true });
      selector.widthInput.dispatchEvent(changeEvent);
      selector.heightInput.dispatchEvent(changeEvent);
      
      console.log(`Testing available size: ${width}×${height}cm`);
    }
  }
};

// Debug function to show current ranges
window.showCustomSizeRanges = function() {
  const element = document.querySelector('[data-custom-size-selector]');
  const selector = element?.customSizeSelector;
  if (selector) {
    console.log('Current custom size ranges:', selector.customSizeRanges);
    
    if (selector.customSizeRanges.length === 0) {
      console.warn('No custom size ranges loaded! This means any custom size will be unavailable.');
      console.log('Make sure you have:');
      console.log('1. Product variants with metafields: custom.size_type = "custom"');
      console.log('2. Width ranges set in: custom.width_range (e.g., "50-200")');
      console.log('3. Height ranges set in: custom.height_range (e.g., "50-300")');
    }
  }
};

// Debug function to check availability of specific size
window.checkSizeAvailability = function(width, height) {
  const element = document.querySelector('[data-custom-size-selector]');
  const selector = element?.customSizeSelector;
  if (selector) {
    const range = selector.findPricingRange(width, height);
    if (range) {
      console.log(`✅ Size ${width}×${height}cm IS AVAILABLE`);
      console.log('Matching range:', range);
    } else {
      console.log(`❌ Size ${width}×${height}cm IS NOT AVAILABLE`);
      console.log('Available ranges:', selector.customSizeRanges);
    }
  }
};

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CustomSizeSelector;
}

// Test dropdown value restoration
window.testDropdownRestoration = function() {
  console.log('🔄 TESTING DROPDOWN VALUE RESTORATION');
  
  const element = document.querySelector('[data-custom-size-selector]');
  const selector = element?.customSizeSelector;
  if (!selector) {
    console.error('Custom size selector not found');
    return;
  }
  
  console.log('Step 1: Switching to standard tab');
  selector.switchTab('standard');
  
  setTimeout(() => {
    console.log('Step 2: Simulating size selection (clicking first available option)');
    const firstOption = selector.standardOptions[0];
    if (firstOption) {
      firstOption.click();
      console.log('Selected size:', firstOption.getAttribute('data-value'));
    }
  }, 1000);
  
  setTimeout(() => {
    console.log('Step 3: Switching to custom tab');
    selector.switchTab('custom');
  }, 2000);
  
  setTimeout(() => {
    console.log('Step 4: Switching back to standard tab - should restore previous selection');
    selector.switchTab('standard');
    console.log('✅ Check if dropdown shows the previously selected value');
  }, 3000);
};

// Test reset functionality
window.testResetSelection = function() {
  console.log('🔄 TESTING SELECTION RESET');
  
  const element = document.querySelector('[data-custom-size-selector]');
  const selector = element?.customSizeSelector;
  if (!selector) {
    console.error('Custom size selector not found');
    return;
  }
  
  console.log('Step 1: Make a selection');
  selector.switchTab('standard');
  
  setTimeout(() => {
    const firstOption = selector.standardOptions[0];
    if (firstOption) {
      firstOption.click();
      console.log('Selected:', selector.selectedStandardValue);
    }
  }, 500);
  
  setTimeout(() => {
    console.log('Step 2: Reset stored selection');
    selector.resetStoredSelection();
  }, 1500);
  
  setTimeout(() => {
    console.log('Step 3: Switch to custom then back to standard - should show "SELECT A SIZE"');
    selector.switchTab('custom');
  }, 2000);
  
  setTimeout(() => {
    selector.switchTab('standard');
    console.log('✅ Should now show "SELECT A SIZE" since selection was reset');
  }, 2500);
};

// Test ATC text restoration with price
window.testATCPriceRestoration = function() {
  console.log('💰 TESTING ATC PRICE RESTORATION ON TAB TOGGLE');
  
  const element = document.querySelector('[data-custom-size-selector]');
  const selector = element?.customSizeSelector;
  if (!selector) {
    console.error('Custom size selector not found');
    return;
  }
  
  console.log('Step 1: Switch to standard and select a size');
  selector.switchTab('standard');
  
  setTimeout(() => {
    const firstOption = selector.standardOptions[0];
    if (firstOption) {
      firstOption.click();
      console.log('✅ Selected standard size - ATC should show original text with price');
    }
  }, 1000);
  
  setTimeout(() => {
    console.log('Step 2: Switch to custom tab');
    selector.switchTab('custom');
    console.log('⚠️  ATC should show "SELECT A SIZE"');
  }, 2500);
  
  setTimeout(() => {
    console.log('Step 3: Enter valid custom dimensions');
    if (selector.widthInput && selector.heightInput) {
      selector.widthInput.value = 100;
      selector.heightInput.value = 150;
      
      const changeEvent = new Event('input', { bubbles: true });
      selector.widthInput.dispatchEvent(changeEvent);
      selector.heightInput.dispatchEvent(changeEvent);
      
      console.log('✅ Entered 100×150cm - ATC should show original text with price');
    }
  }, 4000);
  
  setTimeout(() => {
    console.log('Step 4: Switch back to standard tab');
    selector.switchTab('standard');
    console.log('✅ ATC should show original text with price (restored selection)');
  }, 6000);
  
  setTimeout(() => {
    console.log('Step 5: Switch back to custom tab');
    selector.switchTab('custom');
    console.log('✅ ATC should STILL show original text with price (dimensions preserved)');
  }, 7500);
};

// Utility function to check current ATC button state
window.checkATCState = function() {
  const addToCartButton = document.querySelector('.add-to-cart-button[data-add-to-cart]');
  const addToCartText = document.querySelector('.add-to-cart-button [data-add-to-cart-text]');
  
  if (!addToCartButton || !addToCartText) {
    console.log('❌ ATC button elements not found');
    return;
  }
  
  const isDisabled = addToCartButton.hasAttribute('disabled') || addToCartButton.classList.contains('is-disabled');
  const currentText = addToCartText.textContent;
  const originalText = addToCartText.getAttribute('data-original-text') || 'Not stored';
  
  console.log('🔍 CURRENT ATC BUTTON STATE:');
  console.log(`   Disabled: ${isDisabled}`);
  console.log(`   Current Text: "${currentText}"`);
  console.log(`   Original Text: "${originalText}"`);
  console.log(`   Classes: ${addToCartButton.className}`);
  
  if (!isDisabled && currentText === originalText) {
    console.log('✅ ATC button is in AVAILABLE state with original price text');
  } else if (isDisabled && currentText.includes('SELECT')) {
    console.log('⚠️  ATC button is in SELECT A SIZE state');
  } else if (isDisabled && currentText.includes('UNAVAILABLE')) {
    console.log('❌ ATC button is in UNAVAILABLE state');
  } else {
    console.log('❓ ATC button is in unknown state');
  }
};

// Test separator functionality
window.testSeparatorFormatting = function() {
  console.log('• TESTING SEPARATOR FORMATTING');
  
  const element = document.querySelector('[data-custom-size-selector]');
  const selector = element?.customSizeSelector;
  if (!selector) {
    console.error('Custom size selector not found');
    return;
  }
  
  // Test different text formats
  const testCases = [
    'ADD TO CART',
    'ADD TO CART $45.00',
    'ADD TO CART • $45.00', // Already has separator
    'ADD TO BAG $32.50',
    'Buy Now £25.99'
  ];
  
  console.log('Testing separator formatting for different text formats:');
  testCases.forEach(testText => {
    const shouldAdd = selector.shouldAddSeparator(testText);
    const formatted = selector.formatWithSeparator(testText);
    console.log(`  "${testText}" → Should add: ${shouldAdd} → Result: "${formatted}"`);
  });
  
  // Test with actual ATC button
  const addToCartText = document.querySelector('.add-to-cart-button [data-add-to-cart-text]');
  if (addToCartText) {
    const currentText = addToCartText.textContent;
    console.log(`Current ATC text: "${currentText}"`);
    
    // Simulate updating to available state
    selector.switchTab('standard');
    setTimeout(() => {
      const firstOption = selector.standardOptions[0];
      if (firstOption) {
        firstOption.click();
        console.log(`After selection: "${addToCartText.textContent}"`);
        window.checkATCState();
      }
    }, 500);
  }
};

// Quick separator test
window.testSeparator = function() {
  console.log('• Quick separator test');
  window.testSeparatorFormatting();
};

// Test price updating functionality
window.testPriceUpdates = function() {
  console.log('💰 TESTING PRICE UPDATES ON TAB TOGGLE');
  
  const element = document.querySelector('[data-custom-size-selector]');
  const selector = element?.customSizeSelector;
  if (!selector) {
    console.error('Custom size selector not found');
    return;
  }
  
  console.log('Step 1: Switch to standard and select a size');
  selector.switchTab('standard');
  
  setTimeout(() => {
    const firstOption = selector.standardOptions[0];
    if (firstOption) {
      const sizeValue = firstOption.getAttribute('data-value');
      firstOption.click();
      
      const variant = selector.getVariantBySize(sizeValue);
      const expectedPrice = variant ? selector.formatPrice(variant.price) : 'N/A';
      
      console.log(`✅ Selected standard size: ${sizeValue}`);
      console.log(`💰 Expected price: ${expectedPrice}`);
      window.checkATCState();
      window.checkCurrentPrice();
    }
  }, 1000);
  
  setTimeout(() => {
    console.log('Step 2: Switch to custom tab');
    selector.switchTab('custom');
    console.log('⚠️  Price should be cleared or show SELECT A SIZE');
    window.checkCurrentPrice();
  }, 3000);
  
  setTimeout(() => {
    console.log('Step 3: Enter valid custom dimensions');
    if (selector.widthInput && selector.heightInput) {
      selector.widthInput.value = 100;
      selector.heightInput.value = 150;
      
      const changeEvent = new Event('input', { bubbles: true });
      selector.widthInput.dispatchEvent(changeEvent);
      selector.heightInput.dispatchEvent(changeEvent);
      
      const range = selector.findPricingRange(100, 150);
      const expectedPrice = range ? selector.formatPrice(range.price) : 'N/A';
      
      console.log(`✅ Entered 100×150cm custom size`);
      console.log(`💰 Expected price: ${expectedPrice}`);
      window.checkATCState();
      window.checkCurrentPrice();
    }
  }, 5000);
  
  setTimeout(() => {
    console.log('Step 4: Switch back to standard tab');
    selector.switchTab('standard');
    console.log('💰 Price should restore to previously selected standard size');
    window.checkCurrentPrice();
  }, 7000);
};

// Utility function to check current price displays
window.checkCurrentPrice = function() {
  const addToCartText = document.querySelector('.add-to-cart-button [data-add-to-cart-text]');
  const priceElements = document.querySelectorAll('[data-product-price], .product__price, .price');
  
  console.log('🔍 CURRENT PRICE STATE:');
  
  if (addToCartText) {
    console.log(`   ATC Button: "${addToCartText.textContent}"`);
  }
  
  priceElements.forEach((element, index) => {
    if (element.textContent.trim()) {
      console.log(`   Price Element ${index + 1}: "${element.textContent}" (${element.className})`);
    }
  });
};

// Complete ATC state demonstration
window.demoATCStates = function() {
  console.log('🎯 DEMONSTRATING ALL ATC BUTTON STATES + DROPDOWN RESTORATION + PRICE RESTORATION + SEPARATOR FORMATTING + PRICE UPDATES');
  
  setTimeout(() => {
    console.log('1️⃣ Price update test');
    window.testPriceUpdates();
  }, 1000);
  
  setTimeout(() => {
    console.log('2️⃣ Separator formatting test');
    window.testSeparatorFormatting();
  }, 9000);
  
  setTimeout(() => {
    console.log('3️⃣ SELECT A SIZE state');
    window.testSelectSizeState();
  }, 12000);
  
  setTimeout(() => {
    console.log('4️⃣ UNAVAILABLE state');  
    window.testUnavailableState();
  }, 14000);
  
  setTimeout(() => {
    console.log('5️⃣ AVAILABLE state');
    window.testAvailableState();
  }, 16000);
  
  setTimeout(() => {
    console.log('6️⃣ Dropdown restoration demo');
    window.testDropdownRestoration();
  }, 18000);
  
  setTimeout(() => {
    console.log('7️⃣ Reset selection demo');
    window.testResetSelection();
  }, 22000);
  
  setTimeout(() => {
    console.log('8️⃣ ATC price restoration demo');
    window.testATCPriceRestoration();
  }, 26000);
};
